import {
  AlertTriangle,
  Check,
  Database,
  FileText,
  Globe,
  HelpCircle,
  Plug,
  PlusCircle,
  Search,
  ShieldCheck,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchContextType } from '../../types/conversation';
import {
  ConnectorProvider,
  ConnectorSource,
  KnowledgeSource,
} from '../../types/entities';
import { normalizeTags } from '../../utils';
import { ConnectorLogo, getProviderLabel } from '../connectors';
import { Badge, Button, Input, LoadingSpinner, Modal, Tooltip } from '../ui';

interface KnowledgeBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeSources: KnowledgeSource[];
  selectedKnowledgeSources: KnowledgeSource[];
  isLoadingKnowledge: boolean;
  onToggleKnowledge: (source: KnowledgeSource) => void;
  connectorSources: ConnectorSource[];
  selectedConnectorSources: ConnectorSource[];
  isLoadingConnectors: boolean;
  onToggleConnector: (source: ConnectorSource) => void;
  searchContextType: SearchContextType;
  onSearchContextTypeChange: (value: SearchContextType) => void;
}

interface SearchContextOption {
  value: SearchContextType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const SEARCH_CONTEXT_OPTIONS: SearchContextOption[] = [
  {
    value: 'global',
    label: 'Toda a base',
    description: 'Pesquisa em todos os documentos da organização.',
    icon: Database,
  },
  {
    value: 'specific',
    label: 'Documentos específicos',
    description: 'Selecione apenas os documentos a serem consultados.',
    icon: FileText,
  },
  {
    value: 'connectors',
    label: 'Conectores',
    description: 'Use conectores ativos como fonte (bancos, ERPs, etc).',
    icon: Plug,
  },
  {
    value: 'web',
    label: 'Web',
    description: 'Permite ao agente consultar a internet.',
    icon: Globe,
  },
];

type KnowledgeStatusFilter = 'all' | 'ready' | 'processing' | 'error';

const formatHealthcheckTimestamp = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const HealthStatusBadge: React.FC<{ source: ConnectorSource }> = ({ source }) => {
  const status = source.health_status ?? null;
  const checkedAt = formatHealthcheckTimestamp(source.last_healthcheck_at);

  if (status === 'healthy') {
    return (
      <Tooltip
        content={checkedAt ? `Última verificação: ${checkedAt}` : 'Conector saudável'}
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-500/15 dark:text-green-300">
          <ShieldCheck className="h-3 w-3" />
          OK
        </span>
      </Tooltip>
    );
  }

  if (status === 'error') {
    return (
      <Tooltip
        content={
          source.last_healthcheck_error ||
          (checkedAt ? `Falha em ${checkedAt}` : 'Falha na última verificação')
        }
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
          <AlertTriangle className="h-3 w-3" />
          Erro
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content="Status desconhecido. Execute um teste de conexão.">
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        <HelpCircle className="h-3 w-3" />
        Desconhecido
      </span>
    </Tooltip>
  );
};

const buildConnectorSubtitle = (source: ConnectorSource): string => {
  const config = source.config_json || (source.metadata as ConnectorSource['config_json']) || {};
  const host = typeof config?.host === 'string' ? config.host : null;
  const port = typeof config?.port === 'number' ? config.port : null;
  const database = typeof config?.database === 'string' ? config.database : null;

  const hostPart = host ? (port ? `${host}:${port}` : host) : null;
  const parts = [hostPart, database].filter((part): part is string => Boolean(part));
  if (parts.length === 0) {
    return getProviderLabel(source.provider);
  }
  return parts.join(' • ');
};

const SearchContextCard: React.FC<{
  option: SearchContextOption;
  isActive: boolean;
  onSelect: () => void;
}> = ({ option, isActive, onSelect }) => {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors ${
        isActive
          ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:border-gray-600'
      }`}
      aria-pressed={isActive}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
        <Icon
          className={`h-4 w-4 ${
            isActive
              ? 'text-primary-600 dark:text-primary-300'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        />
        {option.label}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {option.description}
      </p>
    </button>
  );
};

export const KnowledgeBaseModal: React.FC<KnowledgeBaseModalProps> = ({
  isOpen,
  onClose,
  knowledgeSources,
  selectedKnowledgeSources,
  isLoadingKnowledge,
  onToggleKnowledge,
  connectorSources,
  selectedConnectorSources,
  isLoadingConnectors,
  onToggleConnector,
  searchContextType,
  onSearchContextTypeChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docStatusFilter, setDocStatusFilter] = useState<KnowledgeStatusFilter>('all');
  const [connectorSearchQuery, setConnectorSearchQuery] = useState('');
  const [connectorProviderFilter, setConnectorProviderFilter] = useState<
    ConnectorProvider | 'all'
  >('all');

  const selectedKnowledgeIds = useMemo(
    () => new Set(selectedKnowledgeSources.map(item => item.id)),
    [selectedKnowledgeSources]
  );

  const selectedConnectorIds = useMemo(
    () => new Set(selectedConnectorSources.map(item => item.id)),
    [selectedConnectorSources]
  );

  const activeConnectorSources = useMemo(
    () => connectorSources.filter(source => source.is_active),
    [connectorSources]
  );

  const availableConnectorProviders = useMemo(() => {
    const providers = new Set<ConnectorProvider>();
    activeConnectorSources.forEach(source => providers.add(source.provider));
    return Array.from(providers);
  }, [activeConnectorSources]);

  const filteredKnowledgeSources = useMemo(() => {
    const normalizedQuery = docSearchQuery.trim().toLowerCase();
    return knowledgeSources.filter(source => {
      if (normalizedQuery) {
        const tagString = normalizeTags(source.tags || [])
          .join(' ')
          .toLowerCase();
        const matchesQuery =
          source.name.toLowerCase().includes(normalizedQuery) ||
          (source.description || '').toLowerCase().includes(normalizedQuery) ||
          tagString.includes(normalizedQuery);
        if (!matchesQuery) {
          return false;
        }
      }

      if (docStatusFilter === 'ready') {
        const isReady =
          source.processing_status === 'completed' &&
          source.embeddings_status === 'completed';
        if (!isReady) return false;
      }

      if (docStatusFilter === 'processing') {
        const isProcessing =
          source.processing_status === 'processing' ||
          source.embeddings_status === 'processing';
        if (!isProcessing) return false;
      }

      if (docStatusFilter === 'error') {
        const hasError =
          source.processing_status === 'error' ||
          source.embeddings_status === 'error' ||
          Boolean(source.processing_error) ||
          Boolean(source.embeddings_error);
        if (!hasError) return false;
      }

      return true;
    });
  }, [knowledgeSources, docSearchQuery, docStatusFilter]);

  const filteredConnectorSources = useMemo(() => {
    const normalizedQuery = connectorSearchQuery.trim().toLowerCase();
    return activeConnectorSources.filter(source => {
      if (normalizedQuery) {
        const subtitle = buildConnectorSubtitle(source).toLowerCase();
        const matchesQuery =
          source.display_name.toLowerCase().includes(normalizedQuery) ||
          source.provider.toLowerCase().includes(normalizedQuery) ||
          subtitle.includes(normalizedQuery);
        if (!matchesQuery) return false;
      }

      if (
        connectorProviderFilter !== 'all' &&
        source.provider !== connectorProviderFilter
      ) {
        return false;
      }

      return true;
    });
  }, [activeConnectorSources, connectorSearchQuery, connectorProviderFilter]);

  const handleGoToIntegrations = () => {
    onClose();
    navigate('/integrations');
  };

  const renderBody = () => {
    if (searchContextType === 'global') {
      return (
        <InfoPanel
          icon={Database}
          title="Toda a base de documentos"
          message="O agente irá pesquisar livremente em todos os documentos cadastrados da organização. Sem necessidade de selecionar fontes específicas."
        />
      );
    }

    if (searchContextType === 'web') {
      return (
        <InfoPanel
          icon={Globe}
          title="Busca na Web"
          message="O agente irá consultar a internet em tempo real para responder. Documentos e conectores não serão utilizados."
        />
      );
    }

    if (searchContextType === 'specific') {
      if (isLoadingKnowledge) {
        return <LoadingArea />;
      }
      return (
        <DocumentsList
          sources={filteredKnowledgeSources}
          selectedIds={selectedKnowledgeIds}
          searchQuery={docSearchQuery}
          onSearchQueryChange={setDocSearchQuery}
          statusFilter={docStatusFilter}
          onStatusFilterChange={setDocStatusFilter}
          onToggle={onToggleKnowledge}
          hasAnySources={knowledgeSources.length > 0}
        />
      );
    }

    if (isLoadingConnectors) {
      return <LoadingArea />;
    }
    return (
      <ConnectorsList
        sources={filteredConnectorSources}
        selectedIds={selectedConnectorIds}
        searchQuery={connectorSearchQuery}
        onSearchQueryChange={setConnectorSearchQuery}
        providerFilter={connectorProviderFilter}
        onProviderFilterChange={setConnectorProviderFilter}
        availableProviders={availableConnectorProviders}
        onToggle={onToggleConnector}
        hasAnyActiveSources={activeConnectorSources.length > 0}
        onCreateConnector={handleGoToIntegrations}
      />
    );
  };

  const selectionSummary = useMemo(() => {
    if (searchContextType === 'specific') {
      return selectedKnowledgeSources.length === 0
        ? 'Nenhum documento selecionado.'
        : `${selectedKnowledgeSources.length} documento(s) selecionado(s).`;
    }
    if (searchContextType === 'connectors') {
      return selectedConnectorSources.length === 0
        ? 'Nenhum conector selecionado.'
        : `${selectedConnectorSources.length} conector(es) selecionado(s).`;
    }
    return null;
  }, [searchContextType, selectedKnowledgeSources.length, selectedConnectorSources.length]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('conversations.knowledgeBaseTitle', 'Base de Conhecimento')}
      size="lg"
    >
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Tipo de busca
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {SEARCH_CONTEXT_OPTIONS.map(option => (
              <SearchContextCard
                key={option.value}
                option={option}
                isActive={searchContextType === option.value}
                onSelect={() => onSearchContextTypeChange(option.value)}
              />
            ))}
          </div>
        </div>

        {selectionSummary && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {selectionSummary}
          </p>
        )}

        {renderBody()}

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onClose}>
            {t('conversations.confirmSelection')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const LoadingArea: React.FC = () => (
  <div className="flex items-center justify-center p-8">
    <LoadingSpinner />
  </div>
);

const InfoPanel: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  message: string;
}> = ({ icon: Icon, title, message }) => (
  <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/40">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
        {title}
      </h4>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{message}</p>
    </div>
  </div>
);

interface DocumentsListProps {
  sources: KnowledgeSource[];
  selectedIds: Set<number>;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  statusFilter: KnowledgeStatusFilter;
  onStatusFilterChange: (value: KnowledgeStatusFilter) => void;
  onToggle: (source: KnowledgeSource) => void;
  hasAnySources: boolean;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  sources,
  selectedIds,
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  onToggle,
  hasAnySources,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          placeholder="Filtrar por nome, descrição ou tag"
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />
        <select
          value={statusFilter}
          onChange={event =>
            onStatusFilterChange(event.target.value as KnowledgeStatusFilter)
          }
          className="select-control"
        >
          <option value="all">Todos os status</option>
          <option value="ready">Pronto</option>
          <option value="processing">Processando</option>
          <option value="error">Com erro</option>
        </select>
      </div>

      <div className="max-h-96 space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        {sources.length === 0 ? (
          <div className="py-10 text-center text-gray-500 dark:text-gray-400">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800/70">
              <FileText className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p>
              {hasAnySources
                ? 'Nenhuma fonte encontrada para esse filtro.'
                : t('conversations.noKnowledgeSources')}
            </p>
            {!hasAnySources && (
              <p className="mt-1 text-sm">{t('conversations.goToKnowledgeBase')}</p>
            )}
          </div>
        ) : (
          sources.map(source => {
            const isSelected = selectedIds.has(source.id);
            const tags = normalizeTags(source.tags || []);
            return (
              <button
                key={source.id}
                type="button"
                onClick={() => onToggle(source)}
                className={`w-full rounded-xl border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate font-medium text-gray-900 dark:text-white">
                      {source.name}
                    </h4>
                    <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
                      {source.file_type} • {source.file_size} bytes
                    </p>
                    {tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {isSelected ? (
                      <Check className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <div className="h-5 w-5 rounded border-2 border-gray-300 dark:border-gray-600" />
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

interface ConnectorsListProps {
  sources: ConnectorSource[];
  selectedIds: Set<number>;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  providerFilter: ConnectorProvider | 'all';
  onProviderFilterChange: (value: ConnectorProvider | 'all') => void;
  availableProviders: ConnectorProvider[];
  onToggle: (source: ConnectorSource) => void;
  hasAnyActiveSources: boolean;
  onCreateConnector: () => void;
}

const ConnectorsList: React.FC<ConnectorsListProps> = ({
  sources,
  selectedIds,
  searchQuery,
  onSearchQueryChange,
  providerFilter,
  onProviderFilterChange,
  availableProviders,
  onToggle,
  hasAnyActiveSources,
  onCreateConnector,
}) => {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          placeholder="Buscar por nome, provider ou host"
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />
        <select
          value={providerFilter}
          onChange={event =>
            onProviderFilterChange(event.target.value as ConnectorProvider | 'all')
          }
          className="select-control"
        >
          <option value="all">Todos os providers</option>
          {availableProviders.map(provider => (
            <option key={provider} value={provider}>
              {getProviderLabel(provider)}
            </option>
          ))}
        </select>
      </div>

      <div className="max-h-96 space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        {sources.length === 0 ? (
          <div className="py-10 text-center text-gray-500 dark:text-gray-400">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800/70">
              <Plug className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {hasAnyActiveSources
                ? 'Nenhum conector encontrado para esse filtro.'
                : 'Nenhum conector ativo disponível.'}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {hasAnyActiveSources
                ? 'Ajuste os filtros ou crie um novo conector.'
                : 'Cadastre um novo conector para começar a utilizá-lo aqui.'}
            </p>
            <Button size="sm" className="mt-3" onClick={onCreateConnector}>
              <PlusCircle className="mr-1 h-4 w-4" />
              Criar conector
            </Button>
          </div>
        ) : (
          <>
            {sources.map(source => {
              const isSelected = selectedIds.has(source.id);
              const subtitle = buildConnectorSubtitle(source);
              return (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => onToggle(source)}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div
                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border ${
                          isSelected
                            ? 'border-primary-200 bg-primary-100 text-primary-700 dark:border-primary-500/40 dark:bg-primary-500/15 dark:text-primary-300'
                            : 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        <ConnectorLogo
                          provider={source.provider}
                          className="h-5 w-5"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="truncate font-medium text-gray-900 dark:text-white">
                            {source.display_name}
                          </h4>
                          <HealthStatusBadge source={source} />
                        </div>
                        <p className="mt-1 truncate text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {getProviderLabel(source.provider)}
                        </p>
                        <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
                          {subtitle}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                          <Check className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                        </div>
                      ) : (
                        <div className="h-7 w-7 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onCreateConnector}
              >
                <PlusCircle className="mr-1 h-4 w-4" />
                Criar novo conector
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
